import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useBack } from '../hooks';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { useToast } from '../context/ToastContext';
import {
  PackagingLayersEditor, WrappingEditor, WRAP_KEYS, wrappingOf, packagingChain, partsPerLayer, palletHeightOf, palletHeightGap,
  COUNT_WORDS, PACK_PLACE_WORDS, STACK_WORDS,
} from '../components/forms/PartForm';
import { ComplianceFields, complianceLine } from '../components/ComplianceCard';

// Admin → Catalogues → Packaging (the director, 15 Oct 2026): everything about
// packaging in one place.
//   What we pack with      every box, bag, tray, pallet, foil and tape, with
//                          its sizes, weight, supplier, price and compliance
//                          (/packaging, migrations 073-075)
//   How each item is packed  every product and part: its layers, how it stands
//                          on the pallet, the lorry and the wrapping -- the
//                          very fields of the part and the product, read and
//                          saved through the Bulk Tool's routes, so a change
//                          here is the same change as on the part's own page.
// Nothing here is a copy. Directors only (the server says so too).

const TEAL = '#14b8a6';
const BORD = '#2a3040';
const CARD = '#1e2329';
const DARK = '#0f1117';
const MUTED = '#6b7280';
const TEXT = '#f0f2f5';
const AMBER = '#f59e0b';
const RED = '#fca5a5';

const inp = { background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '7px 9px', fontSize: '13px', color: TEXT, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui' };
const lab = { fontSize: '10px', fontWeight: '700', color: MUTED, marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' };
const btn = (primary) => ({
  background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL,
  border: `1px solid ${primary ? TEAL : 'rgba(20,184,166,0.6)'}`, borderRadius: '8px', padding: '7px 14px',
  fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap',
});
const small = { ...btn(false), padding: '3px 10px', fontSize: '11px' };
const chip = (color) => ({ fontSize: '10.5px', color, border: `1px solid ${color}55`, borderRadius: '4px', padding: '1px 6px', whiteSpace: 'nowrap' });
const fmt = (n, d = 3) => (n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString('en-GB', { maximumFractionDigits: d }));
const price = (p) => {
  const s = Number(p).toFixed(4).replace(/0{1,2}$/, '');
  return `€ ${s.replace('.', ',')}`;
};
const date = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
const SIZE_KEYS = ['length_mm', 'width_mm', 'height_mm'];
// The editor's columns: three on a wide screen, fewer on a phone -- the same
// for the details and the compliance, so every box lines up (15 Oct 2026).
const FIELD_GRID = 'repeat(auto-fill, minmax(180px, 1fr))';
const ITEM_FIELDS = ['packaging_layers', 'packing_count_method', 'packing_place', ...WRAP_KEYS];

export default function Packaging() {
  const goBack = useBack('/admin');
  const { toast } = useToast();
  // The floor reads this page; changing it is a director's (3 Nov 2026).
  const canEdit = useAuth().can('manage');
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'items' ? 'items' : 'catalogue';
  const [cat, setCat] = useState(null);           // GET /packaging
  const [editing, setEditing] = useState(null);   // { item | null, category }
  const [wrapEdit, setWrapEdit] = useState(null);

  const load = () => api.get('/api/v1/packaging').then(r => setCat(r.data))
    .catch(err => toast.error(err.response?.data?.error || 'The packaging catalogue could not be read just now.'));
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Opened from Stock on one item (29 Oct 2026: "so the director doesn't need
  // to jump there"): ?container=<id>, or ?tab=items&part=<id> | &product=<id>.
  // Closing or saving its editor goes back to where it was opened from.
  const navigate = useNavigate();
  const location = useLocation();
  const leave = () => { if (location.key !== 'default') navigate(-1); else setParams(tab === 'items' ? { tab: 'items' } : {}); };
  const openRow = params.get('part') ? { kind: 'parts', id: params.get('part') } : params.get('product') ? { kind: 'products', id: params.get('product') } : null;
  useEffect(() => {
    const id = params.get('container');
    if (!cat || !id) return;
    const it = cat.items.find(i => String(i.id) === id);
    if (it && canEdit) setEditing({ item: it, category: it.category, deep: true });
    else toast.error('That packaging item is not in the catalogue any more: pick it from the list below.');
  }, [cat]); // eslint-disable-line

  async function saveWrapping() {
    try {
      const { data } = await api.put('/api/v1/packaging/settings/wrapping', wrapEdit);
      const { changed, ...settings } = data;
      setCat(c => ({ ...c, settings }));
      setWrapEdit(null);
      toast.success(data.changed ? 'Saved' : 'Nothing changed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save');
    }
  }

  // One default per use: the pallet a new pallet layer starts with, and the
  // tape and foils an invoice's weights start with.
  async function makeDefault(i) {
    try {
      await api.put('/api/v1/packaging/settings/defaults', { kind: i.default_kind, id: i.id });
      toast.success(`${i.name} is the default ${cat.default_kinds[i.default_kind]}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save');
    }
  }

  const tabBtn = (key, label) => (
    <button onClick={() => setParams(key === 'items' ? { tab: 'items' } : {})}
      style={{ background: tab === key ? 'rgba(20,184,166,0.12)' : 'transparent', color: tab === key ? TEAL : '#9ca3af', border: `1px solid ${tab === key ? TEAL : BORD}`, borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box', maxWidth: '1240px', margin: '0 auto' }}>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: 0, marginBottom: '8px' }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px', marginRight: '4px' }} />Back
      </button>
      <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px' }}>Packaging</h1>
      <p style={{ color: MUTED, fontSize: '13px', margin: '0 0 14px', maxWidth: '760px' }}>
        Every box, bag, tray, pallet, foil and tape, and how each product and part is packed.{canEdit && ' The same data as the part and product pages and the Bulk Tool: a change here is a change there.'}
      </p>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {tabBtn('catalogue', 'What we pack with')}
        {tabBtn('items', 'How each item is packed')}
      </div>

      {!cat && <div style={{ color: MUTED, fontSize: '13px' }}>Loading…</div>}

      {cat && tab === 'catalogue' && (
        <>
          <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '10px', padding: '10px 14px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', fontSize: '13px' }}>
            <span style={{ color: MUTED }}>Wrapping and taping, worked out for invoice weights and for Stock:</span>
            {wrapEdit ? (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>stretch <input style={{ ...inp, width: '60px' }} value={wrapEdit.stretch_pct ?? ''} onChange={e => setWrapEdit(w => ({ ...w, stretch_pct: e.target.value }))} /> %</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>overlap between turns <input style={{ ...inp, width: '60px' }} value={wrapEdit.overlap_mm ?? ''} placeholder={`${fmt(wrapEdit.overlap_pct)} %`} onChange={e => setWrapEdit(w => ({ ...w, overlap_mm: e.target.value }))} /> mm</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>tape past each edge of a box <input style={{ ...inp, width: '60px' }} value={wrapEdit.tape_over_mm ?? ''} onChange={e => setWrapEdit(w => ({ ...w, tape_over_mm: e.target.value }))} /> mm</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>cover sheet over each side <input style={{ ...inp, width: '60px' }} value={wrapEdit.cover_over_mm ?? ''} placeholder="cut typed" onChange={e => setWrapEdit(w => ({ ...w, cover_over_mm: e.target.value }))} /> mm</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input style={{ ...inp, width: '60px' }} value={wrapEdit.cover_tape_m ?? ''} onChange={e => setWrapEdit(w => ({ ...w, cover_tape_m: e.target.value }))} /> m of
                  <select style={{ ...inp, width: 'auto' }} value={wrapEdit.cover_tape_id ?? ''} onChange={e => setWrapEdit(w => ({ ...w, cover_tape_id: e.target.value }))}>
                    <option value="">no tape</option>
                    {cat.items.filter(i => i.category === 'tape').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  per cover sheet
                </label>
                {[['box_label_id', 'label on each box'], ['pallet_label_id', 'label on each pallet']].map(([k, said]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {said}
                    <select style={{ ...inp, width: 'auto' }} value={wrapEdit[k] ?? ''} onChange={e => setWrapEdit(w => ({ ...w, [k]: e.target.value }))}>
                      <option value="">no label</option>
                      {cat.items.filter(i => (i.category || 'other') === 'other').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                ))}
                <button style={small} onClick={() => setWrapEdit(null)}>Cancel</button>
                <button style={{ ...small, background: TEAL, color: '#0f172a' }} onClick={saveWrapping}>Save</button>
              </>
            ) : (
              <>
                <span>
                  <b>{fmt(cat.settings.stretch_pct)} %</b> stretch · <b>{cat.settings.overlap_mm != null ? `${fmt(cat.settings.overlap_mm)} mm` : `${fmt(cat.settings.overlap_pct)} %`}</b> overlap between turns
                  {' · '}tape <b>{fmt(cat.settings.tape_over_mm)} mm</b> past each edge of a box
                  {cat.settings.cover_over_mm != null && <> · cover sheet <b>{fmt(cat.settings.cover_over_mm)} mm</b> over each side</>}
                  {cat.settings.cover_tape_m != null && <> · <b>{fmt(cat.settings.cover_tape_m)} m</b> of {(cat.items.find(i => String(i.id) === String(cat.settings.cover_tape_id)) || {}).name || 'tape'} per cover sheet</>}
                  {[['box_label_id', 'box'], ['pallet_label_id', 'pallet']].map(([k, on]) => {
                    const it = cat.settings[k] != null && cat.items.find(i => String(i.id) === String(cat.settings[k]));
                    return it ? <Fragment key={k}> · one <b>{it.name}</b> on each {on}</Fragment> : null;
                  })}
                </span>
                {canEdit && <button style={small} onClick={() => setWrapEdit({ ...cat.settings })}>Edit</button>}
              </>
            )}
          </div>

          {cat.categories.map(c => (
            <CategoryCard key={c.value} cat={cat} category={c} items={cat.items.filter(i => (i.category || 'other') === c.value)}
              onAdd={() => setEditing({ item: null, category: c.value })}
              onEdit={(i) => setEditing({ item: i, category: i.category })} onDefault={makeDefault} />
          ))}
        </>
      )}

      {cat && tab === 'items' && <PackedItems containers={cat.items} open={openRow} onLeave={leave} onAddContainer={async (name) => { const r = await api.post('/api/v1/packaging-containers', { name }); await load(); return r.data; }} />}

      {editing && cat && (
        <ItemEditor cat={cat} item={editing.item} category={editing.category}
          onClose={() => { const d = editing.deep; setEditing(null); if (d) leave(); }}
          onSaved={() => { const d = editing.deep; setEditing(null); load(); if (d) leave(); }} />
      )}
    </div>
  );
}

// ── one catalogue item, summed up ───────────────────────────────────────────
function shownValue(fl, v, items) {
  if (v === null || v === undefined || v === '') return null;
  if (fl.kind === 'select') return (fl.options.find(o => o.value === v) || {}).label || v;
  if (fl.kind === 'boolean') return v ? 'Yes' : 'No';
  if (fl.kind === 'ref') return (items.find(x => x.id === v) || {}).name || '(removed)';
  // A pallet's 25 000 g reads as 25 kg.
  if (fl.unit === 'g' && Number(v) >= 1000) return `${fmt(Number(v) / 1000)} kg`;
  if (fl.kind === 'number' || fl.kind === 'integer') return `${fmt(fl.scale ? v / fl.scale : v)}${fl.unit ? ' ' + fl.unit : ''}`;
  return String(v);
}

// 'a box', 'a roll' -- or 'per kg' for foil, bought by weight.
const priceUnit = (category) => {
  const l = category.fields.find(f => f.key === 'price').label;
  return l.startsWith('Price per ') ? l.replace('Price ', '') : l.replace('Price for ', '').replace(/^one /, 'a ');
};

// One kind of packaging as a card, its items as rows (the director, 15 Oct
// 2026): what each is, its details -- a column for each detail any of them has
// -- supplier and price, and which products and parts pack with it.
const shows = (f, item) => !f.showIf || Object.entries(f.showIf).every(([k, v]) => item[k] === v);
const th = { padding: '7px 8px', fontSize: '10.5px', fontWeight: 700, color: MUTED, textAlign: 'left', verticalAlign: 'bottom', borderBottom: `1px solid ${BORD}`, maxWidth: '120px' };
const td = { padding: '8px', verticalAlign: 'top', borderTop: `1px solid ${BORD}` };

function CategoryCard({ cat, category, items, onAdd, onEdit, onDefault }) {
  const canEdit = useAuth().can('manage');
  const fields = category.fields;
  // Length x width x height in one column, for the kinds that have all three;
  // a foil's roll width and cut, or a tape's width, are columns of their own.
  const boxy = SIZE_KEYS.every(k => fields.some(f => f.key === k && /^(Length|Width|Height)/.test(f.label)));
  const sizeField = fields.find(f => f.key === 'length_mm');
  const where = boxy && sizeField && /\((inside|outside)\)/.exec(sizeField.label);
  const hasSize = boxy && items.some(i => SIZE_KEYS.some(k => i[k]));
  const facts = fields.filter(f => !['name', 'code', 'supplier', 'price', 'price_date', ...(boxy ? SIZE_KEYS : [])].includes(f.key))
    .filter(f => items.some(i => shows(f, i) && shownValue(f, i[f.key], cat.items) != null));
  const hasSupplier = items.some(i => i.supplier);
  const hasPrice = items.some(i => i.price != null);
  const perLoad = ['foil', 'tape'].includes(category.value);

  // Every column sorts and filters like Excel's (the director, 15 Oct 2026):
  // `sort` gives the value to order by, `values` what the filter list offers.
  const EMPTY = '(empty)';
  const numberOf = (f, v) => (v === null || v === undefined || v === '' ? null : ['number', 'integer'].includes(f.kind) ? Number(v) : shownValue(f, v, cat.items));
  const columns = [
    { key: 'name', label: 'Name', sort: i => i.name, values: i => [i.name] },
    hasSize && { key: 'size', label: `Size, mm${where ? ` (${where[1]})` : ''}`, sort: i => (SIZE_KEYS.some(k => i[k]) ? SIZE_KEYS.map(k => Number(i[k]) || 0) : null),
      values: i => [SIZE_KEYS.some(k => i[k]) ? SIZE_KEYS.map(k => (i[k] ? fmt(i[k]) : '?')).join(' × ') : EMPTY] },
    ...facts.map(f => ({ key: f.key, label: f.label, sort: i => (shows(f, i) ? numberOf(f, i[f.key]) : null),
      values: i => [(shows(f, i) && shownValue(f, i[f.key], cat.items)) || EMPTY] })),
    hasSupplier && { key: 'supplier', label: 'Supplier', sort: i => i.supplier || null, values: i => [i.supplier || EMPTY] },
    hasPrice && { key: 'price', label: 'Price', right: true, sort: i => (i.price != null ? Number(i.price) : null), values: i => [i.price != null ? price(i.price) : EMPTY] },
    { key: 'used', label: 'Used for', wide: true, sort: i => (i.used_by || []).length,
      values: i => ((i.used_by || []).length ? i.used_by.map(u => u.name) : [perLoad ? 'Chosen per load' : 'Not in any packaging']) },
  ].filter(Boolean);
  const [sort, setSort] = useState(null);         // { key, dir: 1 | -1 }
  const [filters, setFilters] = useState({});     // key -> [values kept]
  const [menu, setMenu] = useState(null);         // { key, rect }
  const shown = useMemo(() => {
    const keep = items.filter(i => columns.every(c => !filters[c.key] || c.values(i).some(v => filters[c.key].includes(v))));
    if (!sort) return keep;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return keep;
    const cmp = (a, b) => (Array.isArray(a) ? a.reduce((r, x, k) => r || (x - b[k]), 0)
      : typeof a === 'number' ? a - b : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
    // Empty cells last, whichever way round.
    return [...keep].sort((x, y) => {
      const a = col.sort(x), b = col.sort(y);
      if (a == null && b == null) return 0;
      if (a == null) return 1;
      if (b == null) return -1;
      return sort.dir * cmp(a, b);
    });
  }, [items, filters, sort, columns]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtering = Object.keys(filters).length > 0;
  const head = (c) => (
    <th key={c.key} style={{ ...th, ...(c.right ? { textAlign: 'right' } : {}), ...(c.wide ? { minWidth: '220px' } : {}) }}>
      <button onClick={e => { const rect = e.currentTarget.getBoundingClientRect(); setMenu(m => (m && m.key === c.key ? null : { key: c.key, rect })); }}
        title="Sort and filter"
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, textAlign: 'inherit',
          color: sort && sort.key === c.key ? TEAL : filters[c.key] ? AMBER : MUTED }}>
        {c.label}
        <span style={{ marginLeft: '4px', fontSize: '9px' }}>
          {sort && sort.key === c.key ? (sort.dir === 1 ? '▲' : '▼') : ''}
          {filters[c.key] ? <i className="fa-solid fa-filter" style={{ marginLeft: '2px' }} /> : !(sort && sort.key === c.key) && <i className="fa-solid fa-caret-down" />}
        </span>
      </button>
    </th>
  );

  return (
    <section style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: items.length ? '6px' : 0, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>
          {category.label} <span style={{ color: MUTED, fontWeight: 500 }}>{filtering ? `${shown.length} of ${items.length}` : items.length}</span>
          {(filtering || sort) && (
            <button onClick={() => { setFilters({}); setSort(null); }} style={{ background: 'none', border: 'none', color: TEAL, fontSize: '12px', fontWeight: 600, cursor: 'pointer', marginLeft: '10px' }}>
              Clear {filtering ? 'filters' : 'sort'}
            </button>
          )}
        </h2>
        <button style={{ ...small, display: canEdit ? undefined : 'none' }} onClick={onAdd}><i className="fa-solid fa-plus" style={{ fontSize: '9px', marginRight: '4px' }} />Add {category.one}</button>
      </div>
      {!items.length && <div style={{ color: MUTED, fontSize: '12px', marginTop: '6px' }}>None yet.</div>}
      {items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', minWidth: '820px' }}>
            <thead>
              <tr>
                {columns.map(head)}
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {!shown.length && (
                <tr><td colSpan={columns.length + 1} style={{ ...td, color: MUTED }}>Nothing matches the filters. <button onClick={() => setFilters({})} style={{ background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: '12.5px', padding: 0 }}>Clear filters</button></td></tr>
              )}
              {shown.map(i => {
                const used = i.used_by || [];
                return (
                  <tr key={i.id}>
                    <td style={{ ...td, minWidth: '160px' }}>
                      {/* Its code small beside the name (086, 29 Oct 2026): names, not codes. */}
                      <div style={{ fontWeight: 700, color: TEXT }}>{i.name}{i.code && <span style={{ fontSize: '11px', color: MUTED, fontWeight: 500, marginLeft: '8px' }}>{i.code}</span>}</div>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '3px' }}>
                        {i.is_default && <span style={chip(TEAL)} title="Used unless another is chosen">Default {cat.default_kinds[i.default_kind]}</span>}
                        {i.compliance && i.compliance.recycle_code && <span style={chip('#6ee7b7')} title={complianceLine(i.compliance) || ''}>{i.compliance.recycle_code.split(/\s[–-]\s/)[0]}</span>}
                        {category.value === 'folding_box' && i.design_file && <DesignLink id={i.id} label="Design" />}
                      </div>
                    </td>
                    {hasSize && <td style={{ ...td, whiteSpace: 'nowrap', color: '#d1d5db' }}>{SIZE_KEYS.some(k => i[k]) ? SIZE_KEYS.map(k => (i[k] ? fmt(i[k]) : '?')).join(' × ') : ''}</td>}
                    {facts.map(f => <td key={f.key} style={{ ...td, color: '#d1d5db', whiteSpace: 'nowrap' }}>{shows(f, i) ? shownValue(f, i[f.key], cat.items) : ''}</td>)}
                    {hasSupplier && <td style={{ ...td, color: '#d1d5db' }}>{i.supplier || ''}</td>}
                    {hasPrice && (
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {i.price != null && <>{price(i.price)} <span style={{ color: MUTED }}>{priceUnit(category)}</span>
                          {i.price_date && <div style={{ fontSize: '11px', color: MUTED }}>as of {date(i.price_date)}</div>}</>}
                      </td>
                    )}
                    <td style={{ ...td, fontSize: '12px', lineHeight: 1.45 }}>
                      {used.length
                        ? used.map((u, k) => <span key={`${u.kind}:${u.id}`}><span style={{ color: u.kind === 'product' ? TEAL : '#e5e7eb' }}>{u.name}</span>{k < used.length - 1 ? ', ' : ''}</span>)
                        : <span style={{ color: MUTED }}>{perLoad ? 'Chosen per load, on the invoice' : 'Not in any packaging yet'}</span>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {canEdit && i.default_kind && !i.is_default && <button onClick={() => onDefault(i)} style={{ ...small, color: '#9ca3af', borderColor: '#374151', marginRight: '6px' }}>Make default</button>}
                      {canEdit && <button style={small} onClick={() => onEdit(i)}>Edit</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {menu && (() => {
        const col = columns.find(c => c.key === menu.key);
        if (!col) return null;
        const all = [...new Set(items.flatMap(col.values))].sort((a, b) => (a === EMPTY) - (b === EMPTY) || a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        return (
          <ColumnMenu rect={menu.rect} all={all} kept={filters[col.key] || null}
            sorted={sort && sort.key === col.key ? sort.dir : 0}
            onSort={(dir) => { setSort(dir ? { key: col.key, dir } : null); setMenu(null); }}
            onKeep={(kept) => setFilters(f => { const n = { ...f }; if (kept) n[col.key] = kept; else delete n[col.key]; return n; })}
            onClose={() => setMenu(null)} />
        );
      })()}
    </section>
  );
}

// The menu under a column's heading: sort either way, and tick which of the
// column's values to show -- as in Excel. Fixed to the screen, so the table's
// sideways scroll does not cut it off; closes on a click elsewhere, Escape or
// scrolling.
function ColumnMenu({ rect, all, kept, sorted, onSort, onKeep, onClose }) {
  const ref = useRef(null);
  const [find, setFind] = useState('');
  useEffect(() => {
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    const away = (e) => { if (!ref.current || !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', out);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', out);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);
  const on = (v) => !kept || kept.includes(v);
  const toggle = (v) => {
    const next = on(v) ? (kept || all).filter(x => x !== v) : [...(kept || []), v];
    onKeep(next.length === all.length ? null : next);
  };
  const list = find ? all.filter(v => v.toLowerCase().includes(find.toLowerCase())) : all;
  const item = (active) => ({ display: 'block', width: '100%', textAlign: 'left', background: active ? 'rgba(20,184,166,0.12)' : 'none', border: 'none', color: active ? TEAL : TEXT, padding: '6px 10px', fontSize: '12.5px', cursor: 'pointer', borderRadius: '6px' });
  const width = 260;
  return (
    <div ref={ref} style={{ position: 'fixed', top: Math.min(rect.bottom + 4, window.innerHeight - 340), left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), width,
      background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', padding: '6px', zIndex: 60, fontFamily: 'system-ui' }}>
      <button style={item(sorted === 1)} onClick={() => onSort(sorted === 1 ? 0 : 1)}><i className="fa-solid fa-arrow-up-short-wide" style={{ width: '18px' }} />Sort ascending</button>
      <button style={item(sorted === -1)} onClick={() => onSort(sorted === -1 ? 0 : -1)}><i className="fa-solid fa-arrow-down-wide-short" style={{ width: '18px' }} />Sort descending</button>
      <div style={{ borderTop: `1px solid ${BORD}`, margin: '6px 0' }} />
      {all.length > 8 && <input autoFocus value={find} onChange={e => setFind(e.target.value)} placeholder="Find…" style={{ ...inp, padding: '5px 8px', fontSize: '12px', marginBottom: '4px' }} />}
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 10px', fontSize: '12.5px', color: TEXT, cursor: 'pointer' }}>
        <input type="checkbox" checked={!kept} onChange={() => onKeep(kept ? null : [])} /> Select all
      </label>
      <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
        {list.map(v => (
          <label key={v} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '4px 10px', fontSize: '12.5px', color: v === '(empty)' ? MUTED : '#e5e7eb', cursor: 'pointer' }}>
            <input type="checkbox" checked={on(v)} onChange={() => toggle(v)} style={{ marginTop: '2px' }} /> <span>{v}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function DesignLink({ id, label = 'Open the design' }) {
  const { toast } = useToast();
  async function open() {
    const w = window.open('', '_blank');
    try {
      const { data } = await api.get(`/api/v1/packaging/${id}/design`);
      if (w) w.location = data.url; else window.location.href = data.url;
    } catch (err) {
      if (w) w.close();
      toast.error(err.response?.data?.error || 'The design could not be opened just now.');
    }
  }
  return <button onClick={open} style={{ ...chip(TEAL), background: 'none', cursor: 'pointer' }}><i className="fa-solid fa-file-pdf" style={{ marginRight: '4px' }} />{label}</button>;
}

// ── add or edit a catalogue item ────────────────────────────────────────────
function startValues(item, fields) {
  const out = {};
  for (const fl of fields) {
    const v = item ? item[fl.key] : null;
    out[fl.key] = v === null || v === undefined ? '' : (fl.scale ? String(v / fl.scale) : fl.kind === 'boolean' ? (v ? 'true' : 'false') : String(v));
  }
  return out;
}

function ItemEditor({ cat, item, category: startCategory, onClose, onSaved }) {
  const { toast } = useToast();
  const [category, setCategory] = useState(item ? item.category : startCategory);
  const spec = cat.categories.find(c => c.value === category) || cat.categories.find(c => c.value === 'other');
  const allFields = useMemo(() => [...new Map(cat.categories.flatMap(c => c.fields).map(f => [f.key, f])).values()], [cat]);
  const [start] = useState(() => startValues(item, allFields));
  const [values, setValues] = useState(start);
  const [comp, setComp] = useState(() => ({ ...(item ? item.compliance : {}) }));
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const set = (k, v) => setValues(x => ({ ...x, [k]: v }));
  const shown = spec.fields.filter(f => !f.showIf || Object.entries(f.showIf).every(([k, v]) => values[k] === v));

  async function save() {
    setBusy(true);
    try {
      const sent = {};
      if (!item || category !== item.category) sent.category = category;
      for (const fl of shown) if (!item || values[fl.key] !== start[fl.key]) sent[fl.key] = fl.kind === 'boolean' ? (values[fl.key] === '' ? null : values[fl.key] === 'true') : values[fl.key];
      const compliance = Object.fromEntries(cat.compliance_fields.map(f => f.key)
        .filter(k => (comp[k] || null) !== ((item && item.compliance[k]) || null)).map(k => [k, comp[k] || null]));
      if (item) {
        const { data } = await api.put(`/api/v1/packaging/${item.id}`, { values: sent, compliance, expect_updated_at: item.updated_at });
        toast.success(data.changed ? `${data.name} saved` : 'Nothing changed');
      } else {
        const { data } = await api.post('/api/v1/packaging', { values: sent, compliance });
        toast.success(`${data.name} added`);
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save');
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.delete(`/api/v1/packaging/${item.id}`);
      toast.success(`${item.name} taken out of the catalogue`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove it');
      setBusy(false);
      setConfirmRemove(false);
    }
  }

  async function uploadDesign(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      await api.post(`/api/v1/packaging/${item.id}/design`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Design uploaded');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'The design could not be uploaded');
      setBusy(false);
    }
  }

  const field = (fl) => {
    const v = values[fl.key];
    let control;
    if (fl.kind === 'select') {
      control = (
        <select style={inp} value={v} onChange={e => set(fl.key, e.target.value)}>
          <option value="">{fl.required ? 'Pick one…' : 'Not said'}</option>
          {fl.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    } else if (fl.kind === 'boolean') {
      control = (
        <select style={inp} value={v} onChange={e => set(fl.key, e.target.value)}>
          <option value="">Not said</option><option value="true">Yes</option><option value="false">No</option>
        </select>
      );
    } else if (fl.kind === 'ref') {
      const fits = cat.items.filter(x => x.category === fl.refTo.category && (!fl.refTo.foil_type || x.foil_type === fl.refTo.foil_type) && (!item || x.id !== item.id));
      control = (
        <select style={inp} value={v} onChange={e => set(fl.key, e.target.value)}>
          <option value="">{fl.none || (fits.length === 1 ? `Not set (the only one, ${fits[0].name}, is used)` : 'Not set')}</option>
          {fits.map(x => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
        </select>
      );
    } else if (fl.kind === 'date') {
      control = <input type="date" style={inp} value={v} onChange={e => set(fl.key, e.target.value)} />;
    } else {
      control = (
        <div style={{ position: 'relative' }}>
          <input style={{ ...inp, textAlign: fl.kind === 'text' ? 'left' : 'right', ...(fl.unit ? { paddingRight: `${22 + fl.unit.length * 7}px` } : {}) }} value={v} list={fl.suggest ? `sup-${category}` : undefined}
            onChange={e => set(fl.key, e.target.value)} />
          {fl.unit && <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: MUTED, pointerEvents: 'none', whiteSpace: 'nowrap' }}>{fl.unit}</span>}
        </div>
      );
    }
    return (
      <div key={fl.key} style={{ gridColumn: fl.key === 'name' ? '1 / -1' : undefined }}>
        <span style={lab}>{fl.label}{fl.directorOnly ? ' 🔒' : ''}</span>{control}
      </div>
    );
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 12px', overflowY: 'auto', zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '18px', width: '100%', maxWidth: '640px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700 }}>{item ? item.name : `New ${spec.one}`}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: MUTED, fontSize: '16px', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <span style={lab}>Kind</span>
          <select style={inp} value={category} onChange={e => setCategory(e.target.value)}>
            {cat.categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <datalist id={`sup-${category}`}>{(cat.suppliers[category] || []).map(sname => <option key={sname} value={sname} />)}</datalist>
        <div style={{ display: 'grid', gridTemplateColumns: FIELD_GRID, gap: '10px 12px' }}>
          {shown.map(field)}
        </div>

        {category === 'folding_box' && (
          <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px solid ${BORD}` }}>
            <span style={lab}>Design (PDF)</span>
            {item ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px' }}>
                {item.design_file ? <DesignLink id={item.id} /> : <span style={{ color: MUTED }}>None uploaded yet.</span>}
                <label style={{ ...small, display: 'inline-block' }}>
                  {item.design_file ? 'Upload a new one' : 'Upload'}
                  <input type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => uploadDesign(e.target.files[0])} />
                </label>
              </div>
            ) : <div style={{ fontSize: '12px', color: MUTED }}>Save the box first, then upload its design.</div>}
          </div>
        )}

        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px solid ${BORD}` }}>
          <span style={{ ...lab, marginBottom: '8px' }}>Recycling &amp; compliance 🔒</span>
          <ComplianceFields fields={cat.compliance_fields} values={comp} onChange={(k, v) => setComp(c => ({ ...c, [k]: v }))} />
        </div>

        {item && (item.used_by || []).length > 0 && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: MUTED }}>
            In the packaging of: {item.used_by.map(u => u.name).join(', ')}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <div>
            {item && !confirmRemove && <button style={{ ...small, color: RED, borderColor: '#7f1d1d' }} disabled={busy} onClick={() => setConfirmRemove(true)}>Remove…</button>}
            {item && confirmRemove && (
              <span style={{ fontSize: '12px', color: RED, display: 'inline-flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                Take {item.name} out of the catalogue?
                <button style={{ ...small, color: RED, borderColor: '#7f1d1d' }} disabled={busy} onClick={remove}>Yes, remove it</button>
                <button style={small} onClick={() => setConfirmRemove(false)}>No</button>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...btn(false), color: '#9ca3af', borderColor: '#374151' }} onClick={onClose}>Cancel</button>
            <button style={btn(true)} disabled={busy} onClick={save}>{busy ? 'Saving…' : item ? 'Save' : 'Add'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── how each product and part is packed ─────────────────────────────────────
const ITEM_QUERY = `fields=${ITEM_FIELDS.join(',')}`;

function PackedItems({ containers, onAddContainer, open = null, onLeave }) {
  const canEdit = useAuth().can('manage');
  const { toast } = useToast();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [show, setShow] = useState('sold');      // sold | stored | all
  const [missingOnly, setMissingOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deep, setDeep] = useState(false);       // opened from Stock: closing goes back
  useEffect(() => {
    if (!rows || !open) return;
    const r = rows.find(x => x.kind === open.kind && String(x.id) === String(open.id));
    if (r) { setEditing(r); setDeep(true); } else toast.error('That item is not in the list any more: find it below.');
  }, [rows]); // eslint-disable-line
  const close = () => { setEditing(null); if (deep) { setDeep(false); onLeave(); } };

  useEffect(() => {
    Promise.all([api.get(`/api/v1/bulk/products/items?${ITEM_QUERY}`), api.get(`/api/v1/bulk/parts/items?${ITEM_QUERY}`)])
      .then(([pr, pa]) => setRows([
        ...pr.data.items.map(i => ({ ...i, kind: 'products', stored: false })),
        ...pa.data.items.map(i => ({ ...i, kind: 'parts', stored: !!i.in_product })),
      ]))
      .catch(err => toast.error(err.response?.data?.error || 'The parts and products could not be read just now.'));
  }, []); // eslint-disable-line

  const missing = (r) => {
    const v = r.values;
    const layers = v.packaging_layers || [];
    const out = [];
    if (!layers.length) return ['no packaging'];
    if (layers.some(l => l.container_id == null)) out.push('a container not named');
    if (layers.some(l => !l.qty)) out.push('a quantity missing');
    if (r.stored) return out;
    const h = palletHeightOf(layers, containers, v.pallet_height_mm);
    if (!h) out.push('no pallet layer');
    else {
      if (!v.wrap_direction) out.push('wrapping not set');
      if (!h.mm) out.push(`pallet height unknown: it ${palletHeightGap(layers, containers) || 'needs the layers filled in'}`);
    }
    return out;
  };

  const shownRows = (rows || [])
    .filter(r => (show === 'all' ? true : show === 'stored' ? r.stored : !r.stored))
    .filter(r => !q || `${r.title} ${r.subtitle || ''}`.toLowerCase().includes(q.toLowerCase()))
    .filter(r => !missingOnly || missing(r).length)
    .sort((a, b) => (a.kind === b.kind ? a.title.localeCompare(b.title) : a.kind === 'products' ? -1 : 1));

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
        <input style={{ ...inp, maxWidth: '260px' }} placeholder="Find a product or part…" value={q} onChange={e => setQ(e.target.value)} />
        <select style={{ ...inp, width: 'auto' }} value={show} onChange={e => setShow(e.target.value)}>
          <option value="sold">Products, and parts sold as they are</option>
          <option value="stored">Parts stored until assembly</option>
          <option value="all">Everything</option>
        </select>
        <label style={{ fontSize: '12.5px', color: '#d1d5db', display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input type="checkbox" checked={missingOnly} onChange={e => setMissingOnly(e.target.checked)} /> Only those missing something
        </label>
        {rows && <span style={{ fontSize: '12px', color: MUTED }}>{shownRows.length} shown</span>}
      </div>
      {!rows && <div style={{ color: MUTED, fontSize: '13px' }}>Loading…</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {shownRows.map(r => {
          const v = r.values;
          const layers = v.packaging_layers || [];
          const h = palletHeightOf(layers, containers, v.pallet_height_mm);
          const per = partsPerLayer(layers);
          const gaps = missing(r);
          return (
            <div key={`${r.kind}:${r.id}`} style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '10px', padding: '10px 14px', display: 'flex', gap: '12px', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: '1 1 420px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px' }}>
                  <span style={{ color: r.kind === 'products' ? TEAL : TEXT }}>{r.title}</span>
                  {r.subtitle && <span style={{ fontSize: '11px', color: MUTED, fontWeight: 500, marginLeft: '8px' }}>{r.subtitle}</span>}
                  <span style={{ fontSize: '10.5px', color: MUTED, fontWeight: 500, marginLeft: '8px' }}>{r.kind === 'products' ? 'product' : r.stored ? 'part, stored until assembly' : 'part'}</span>
                </div>
                <div style={{ fontSize: '12.5px', color: '#d1d5db', marginTop: '3px' }}>{layers.length ? packagingChain(layers, containers) : <span style={{ color: MUTED }}>No packaging entered yet.</span>}</div>
                {h && !r.stored && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '3px' }}>
                    {per[h.pi] ? `${fmt(per[h.pi], 0)} a pallet · ` : ''}{STACK_WORDS[v.pallet_stacking || 'none'].toLowerCase()}
                    {v.wrap_direction ? ` · wrapped ${v.wrap_direction === 'up_down' ? 'up and down' : 'up only'}${v.wrap_rotation_speed != null || v.wrap_lift_speed != null ? ` at ${v.wrap_rotation_speed ?? '?'}–${v.wrap_lift_speed ?? '?'}` : ''}` : ''}
                    {h.mm ? ` · ${fmt(h.mm, 0)} mm high` : ''}
                    {v.box_extra_tape ? ' · extra tape' : ''}
                  </div>
                )}
                {gaps.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '5px' }}>{gaps.map(g => <span key={g} style={chip(AMBER)}>{g}</span>)}</div>
                )}
              </div>
              {canEdit && <button style={small} onClick={() => setEditing(r)}>Edit packing</button>}
            </div>
          );
        })}
      </div>
      {editing && (
        <PackingEditor row={editing} containers={containers} onAddContainer={onAddContainer} onClose={close}
          onSaved={(back) => { setRows(rs => rs.map(x => (x.kind === editing.kind && x.id === editing.id ? { ...x, ...back } : x))); close(); }} />
      )}
    </>
  );
}

// One product's or part's packing, saved to the product or part itself.
function PackingEditor({ row, containers, onAddContainer, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState(() => ({ ...row.values, ...wrappingOf(row.values), packaging_layers: row.values.packaging_layers || [] }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const unit = row.kind === 'products' ? 'products' : 'parts';

  async function save() {
    const was = { ...row.values, ...wrappingOf(row.values) };
    const changed = Object.fromEntries(ITEM_FIELDS.filter(k => JSON.stringify(form[k] ?? null) !== JSON.stringify(was[k] ?? null)).map(k => [k, form[k] ?? null]));
    if (!Object.keys(changed).length) { onClose(); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/api/v1/bulk/${row.kind}/${row.id}`, { values: changed, expect_updated_at: row.updated_at, via: 'Packaging' });
      toast.success(`${row.title} saved`);
      onSaved({ updated_at: data.updated_at, values: { ...row.values, ...form, ...(data.values || {}) } });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save');
      setBusy(false);
    }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 12px', overflowY: 'auto', zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '18px', width: '100%', maxWidth: '760px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700 }}>{row.title}</div>
            <div style={{ fontSize: '12px', color: MUTED }}>{row.stored ? 'Goes into a product: how it is kept until assembly.' : `How the ${unit === 'products' ? 'product' : 'part'} is packed, from the ${unit === 'products' ? 'product' : 'part'} outwards.`} The same fields as on its own page.</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: MUTED, fontSize: '16px', cursor: 'pointer' }}>✕</button>
        </div>
        <PackagingLayersEditor layers={form.packaging_layers} containers={containers} unit={unit}
          label={row.stored ? 'Stored in — from the part outwards' : `Packed as — from the ${unit === 'products' ? 'product' : 'part'} outwards`}
          onChange={layers => set('packaging_layers', layers)} onAddContainer={onAddContainer} />
        {!row.stored && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 12px' }}>
            <div>
              <span style={lab}>How the count is checked</span>
              <select style={inp} value={form.packing_count_method || ''} onChange={e => set('packing_count_method', e.target.value || null)}>
                <option value="">Not said</option>
                {Object.entries(COUNT_WORDS).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
              </select>
            </div>
            <div>
              <span style={lab}>Where it is packed</span>
              <select style={inp} value={form.packing_place || ''} onChange={e => set('packing_place', e.target.value || null)}>
                <option value="">Not said</option>
                {Object.entries(PACK_PLACE_WORDS).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
              </select>
            </div>
          </div>
        )}
        {!row.stored && <WrappingEditor form={form} set={set} containers={containers} />}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button style={{ ...btn(false), color: '#9ca3af', borderColor: '#374151' }} onClick={onClose}>Cancel</button>
          <button style={btn(true)} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
